// src/routeMVP1010/RequireDailyCheckIn.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { hasLocalMoodCheckedThisWeek, getMoodWeekKey } from "../utils/moodLocal";
import api from "../utils/api";

// Use centralized API client - paths start with /api/
const getApiBase = () => {
  return api.defaults.baseURL ? `${api.defaults.baseURL}/api` : `${window.location.origin}/api`;
};

// Weekly check-in gate: ensures user has completed at least one MoodCall this week
// (Week starts Monday 00:00 in user's timezone)
export default function RequireDailyCheckIn({
  children,
  moodcallPath = "/MoodCall",
  useLocalFastPass = true,
  apiBase = getApiBase(),
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const tz = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago",
    []
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const goToCheckin = () => {
      if (!cancelled && location.pathname !== moodcallPath) {
        navigate(moodcallPath, { replace: true, state: { from: location } });
      }
    };

    const decide = async () => {
      const token = localStorage.getItem("authToken");
      // If not logged in, go to login (don't allow MoodCall for guests)
      if (!token) {
        navigate("/", { replace: true, state: { from: location } });
        if (!cancelled) setReady(true);
        return;
      }

      // Fast pass check: if user just completed moodcall, skip check
      const fastPassUntil = Number(sessionStorage.getItem("bp_mood_fastpass_until") || "0");
      const hasFastPass = Date.now() < fastPassUntil;
      if (hasFastPass) {
        if (!cancelled) setReady(true);
        // Optional: run background revalidation (non-blocking)
        // IMPORTANT: if server says needs_checkin=true, DO NOT redirect while fastpass is active.
        // Just cache it so the next navigation can enforce.
        fetch(`${apiBase}/mood/status/?tz=${encodeURIComponent(tz)}`, {
          headers: { Authorization: `Token ${token}` },
          credentials: "include"
        }).then(res => {
          if (res.ok) {
            return res.json();
          }
        }).then(json => {
          if (json) {
            const weekKey = getMoodWeekKey(tz);
            const cacheKey = `bp_mood_status_cache_${tz}_${weekKey}`;
            sessionStorage.setItem(cacheKey, JSON.stringify({
              ts: Date.now(),
              needs_checkin: json.needs_checkin || false
            }));
          }
        }).catch(() => {
          // Ignore background fetch errors
        });
        return;
      }

      // Check cache before fetching (week-aware cache key)
      const weekKey = getMoodWeekKey(tz);
      const cacheKey = `bp_mood_status_cache_${tz}_${weekKey}`;
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        try {
          const cacheData = JSON.parse(cached);
          const cacheAge = Date.now() - cacheData.ts;
          if (cacheAge < 60_000) { // 1 minute cache
            if (cacheData.needs_checkin) {
              goToCheckin();
              if (!cancelled) setReady(true);
              return;
            }
            if (!cancelled) setReady(true);
            return;
          }
        } catch {
          // Invalid cache, continue to fetch
        }
      }

      // 1) SERVER = source of truth when authenticated
      if (token) {
        try {
          const res = await fetch(
            `${apiBase}/mood/status/?tz=${encodeURIComponent(tz)}`,
            { headers: { Authorization: `Token ${token}` }, credentials: "include" }
          );
          if (res.ok) {
            const json = await res.json();
            // Cache the result (week-aware cache key)
            sessionStorage.setItem(cacheKey, JSON.stringify({
              ts: Date.now(),
              needs_checkin: json.needs_checkin || false
            }));
            
            if (json?.needs_checkin) {
              goToCheckin();         // server says you still need to check in
              if (!cancelled) setReady(true);
              return;
            }
            if (!cancelled) setReady(true); // server says you're good
            return;
          }
        } catch {
          // fall through to local logic
        }
      }

      // 2) Fallback to local flag ONLY if server unavailable
      if (useLocalFastPass) {
        const ok = hasLocalMoodCheckedThisWeek(tz);
        if (!ok) goToCheckin();
        if (!cancelled) setReady(true);
      } else {
        goToCheckin();
        if (!cancelled) setReady(true);
      }
    };

    decide();
    return () => { cancelled = true; };
  }, [apiBase, tz, navigate, location, moodcallPath, useLocalFastPass]);

  if (!ready) {
    return <div style={{ padding: 12, textAlign: "center", color: "#6b7280" }}>Loading…</div>;
  }
  return children;
}
